import { Box, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Eye, SearchNormal1, Send2 } from 'iconsax-reactjs';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import CustomTable from 'components/@extended/CustomTable';
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import SkeletonTable from 'components/SkeletonTable';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { getAssessorWiseDetails } from 'services/assessorDetails.service';
import PerformanceCrieteria from './performance-criteria-model';
import useAuth from 'hooks/useAuth';
import { hasNcvetQuestionBankAccess } from 'utils/ncvetQuestionBankAccess';

interface QualificationPack {
  _id: string;
  qpCode: string;
  qpName: string;
  nsqfLevel: number;
}

interface BatchInfo {
  _id: string;
  name: string;
  batchStartDate: string;
  batchEndDate: string;
}

interface NosItem {
  _id: string;
  nosCode: string;
  nosName: string;
  totalMarks: number;
}

interface PcItem {
  _id: string;
  pcCode: string;
  pcName: string;
  totalMarks: number;
  isCritical: boolean;
  type: string;
}

interface AssessorTemplate {
  _id: string;
  templateName: string;
  startDate: string;
  endDate: string;
  type: string;
  attemptNumber?: number;
  qualificationPack: QualificationPack;
  batch: BatchInfo;
  nos: NosItem[];
  pcs: PcItem[];
  assignedAssessorId?: string;
  resultsReleased?: boolean;
  sessionStatus?: string;
}

const isPerformanceEvaluationDone = (row: Pick<AssessorTemplate, 'resultsReleased' | 'sessionStatus'>) =>
  Boolean(row.resultsReleased) || String(row.sessionStatus || '').toLowerCase() === 'completed';

const formatDate = (value?: string) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';

  return d.toLocaleDateString();
};

const AssessmentTemplate = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<AssessorTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [total, setTotal] = useState(0);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sort, setSort] = useState<Array<{ id: string; sort?: boolean; desc?: boolean }>>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AssessorTemplate | null>(null);
  const [initialTableReady, setInitialTableReady] = useState(false);

  const handleViewDetails = useCallback((item: AssessorTemplate) => {
    setSelectedTemplate(item);
    setOpenDialog(true);
  }, []);

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(value);
      setPageIndex(1);
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const sortItem = Array.isArray(sort) && sort.length > 0 ? sort[0] : null;
        const sortColumn = sortItem?.id;
        const sortOrder = sortItem ? (sortItem.desc ? 'desc' : 'asc') : undefined;

        const query = {
          page: pageIndex,
          limit: pageSize,
          search: search || undefined,
          sortColumn,
          sortOrder
        };
        const res = await getAssessorWiseDetails(query);
        setTemplates(res?.data?.data?.data || []);
        setTotal(res?.data?.data?.total || 0);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        setInitialTableReady(true);
      }
    };

    fetchTemplates();
  }, [pageIndex, pageSize, search, sort]);

  const columns = useMemo(
    () => [
      {
        id: 'batch',
        header: 'BATCH',
        accessorKey: 'batch.name',
        cell: ({ row }: any) => {
          const batch: BatchInfo | undefined = row.original.batch;
          return batch?.name ?? '-';
        }
      },
      {
        id: 'qualificationPack',
        header: 'QUALIFICATION PACK',
        accessorKey: 'qualificationPack.qpName',

        cell: ({ row }: any) => {
          const item: AssessorTemplate = row.original;
          return item.qualificationPack.qpName;
        }
      },
      {
        id: 'templateName',
        header: 'TEMPLATE NAME',
        accessorKey: 'templateName'
      },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'type',
        cell: ({ row }: any) => {
          const item: AssessorTemplate = row.original;
          const formattedType = item.type ? item.type.replace(/^./, (char: string) => char.toUpperCase()) : '-';

          return `${formattedType}`;
        }
      },
      {
        id: 'attemptNumber',
        header: 'Attempt',
        accessorKey: 'attemptNumber',
        cell: ({ row }: any) => {
          const item: AssessorTemplate = row.original;
          return item.attemptNumber ? `${item.attemptNumber}` : '-';
        }
      },
      {
        id: 'startDate',
        header: 'START DATE',
        accessorKey: 'startDate',
        cell: ({ row }: any) => {
          const item: AssessorTemplate = row.original;
          return formatDate(item.startDate);
        }
      },
      {
        id: 'endDate',
        header: 'END DATE',
        accessorKey: 'endDate',
        cell: ({ row }: any) => {
          const item: AssessorTemplate = row.original;
          return formatDate(item.endDate);
        }
      },
      {
        id: 'actions',
        header: 'OPTIONS',
        enableSorting: false,
        cell: ({ row }: any) => {
          const item = row.original as AssessorTemplate;
          const currentUserId = String(user?._id ?? user?.id ?? '');
          const assigned =
            item.assignedAssessorId != null && item.assignedAssessorId !== '' ? String(item.assignedAssessorId) : '';
          const isOwnSession = Boolean(currentUserId && assigned && assigned === currentUserId);
          const performanceDone = isPerformanceEvaluationDone(item);
          const canAddQuestions = isOwnSession && hasNcvetQuestionBankAccess(user) && !performanceDone;
          return (
            <Stack direction="row" spacing={1}>
              <Tooltip title="View details">
                <IconButton color="primary" onClick={() => handleViewDetails(row.original)}>
                  <Eye />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  !hasNcvetQuestionBankAccess(user)
                    ? 'Your organization has not enabled question bank access for your role'
                    : performanceDone
                      ? 'Performance evaluation is complete for this session; questions cannot be added'
                      : !assigned || !isOwnSession
                        ? 'Only the assessor assigned to this batch/session can add questions'
                        : 'Add Questions'
                }
              >
                <span>
                  <IconButton
                    color="primary"
                    disabled={!canAddQuestions}
                    onClick={() =>
                      canAddQuestions &&
                      navigate('/assessment-details/questions', {
                        state: { templateDetails: item }
                      })
                    }
                  >
                    <Send2 />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        }
      }
    ],
    [handleViewDetails, navigate, user]
  );

  const handleSortingData = useCallback((payload: { name: string }) => {
    setSort((prev) => {
      const existing = prev.find((s) => s.id === payload.name);

      if (!existing) {
        return [{ id: payload.name, sort: true, desc: false }];
      }
      if (existing.sort && !existing.desc) {
        return [{ id: payload.name, sort: true, desc: true }];
      }
      if (existing.sort && existing.desc) {
        return [];
      }

      return prev;
    });

    setPageIndex(1);
  }, []);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <MainCard
        title={
          <Stack direction="row" alignItems="center" spacing={2}>
            <BackToggle show />
            <Box>
              <Typography variant="h5">
                <FormattedMessage id="Assessment Details" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your Assessment Templates
              </Typography>
            </Box>
          </Stack>
        }
        content={false}
        secondary={
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Search templates..."
              size="small"
              value={searchInput}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                  setSearch(searchInput);
                  setPageIndex(1);
                }
              }}
            />
            <IconButton variant="outlined">
              <SearchNormal1 />
            </IconButton>
          </Stack>
        }
      />

      <MainCard content={false}>
        {!initialTableReady && loading ? (
          <SkeletonTable columns={columns} />
        ) : (
          <CustomTable
            columns={columns}
            data={templates}
            sortArray={['templateName', 'qualificationPack', 'batch', 'assessmentWindow', 'startDate', 'endDate']}
            sort={sort}
            setSortingData={handleSortingData}
            striped
            count={total}
            size={pageSize}
            pageIndex={pageIndex}
            handlePageChange={(_event: unknown, page: number) => setPageIndex(page)}
            handleSizeChange={(e: any) => {
              setPageSize(Number(e.target.value));
              setPageIndex(1);
            }}
            notFound={templates.length === 0 && !loading}
            search={search}
            loading={initialTableReady && loading}
          />
        )}
      </MainCard>
      {openDialog && selectedTemplate && (
        <PerformanceCrieteria openDialog={openDialog} setOpenDialog={setOpenDialog} selectedTemplate={selectedTemplate} />
      )}
    </Box>
  );
};

export default AssessmentTemplate;
